import React, { useRef, useState, useEffect } from 'react';
import { Save, Plus, User, MapPin, CheckCircle, AlertCircle, Upload, Image, Search, PenTool } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { getAssetUrl } from '../../utils/assetUrl';
import { useTranslation } from 'react-i18next';
import SignaturePad, { SignaturePadHandle } from '../ContratoAssinatura/SignaturePad';
import { ActionButton, Button, RowActions, Tabs } from '../../components/UI';

interface AdvogadoData {
  _id?: string;
  nome: string;
  cedulaProfissional: string;
  oabNumero: string;
  oabEstado: string;
  endereco: {
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    cep: string;
    pais: string;
  };
  telefone?: string;
  email?: string;
  iban?: string;
  logo?: string;
  assinatura?: {
    tipo?: 'drawn' | 'typed' | null;
    imageData?: string;
    typedName?: string;
  };
  isActive?: boolean;
  ocultarDadosContrato?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const emptyFormData = (): AdvogadoData => ({
  nome: '',
  cedulaProfissional: '',
  oabNumero: '',
  oabEstado: '',
  endereco: {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    cep: '',
    pais: 'Portugal'
  },
  telefone: '',
  email: '',
  iban: '',
  logo: '',
  assinatura: { tipo: null, imageData: '', typedName: '' },
  ocultarDadosContrato: false
});

const AdminAdvogado: React.FC = () => {
  const { t } = useTranslation();
  const [advogados, setAdvogados] = useState<AdvogadoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [searchingCEP, setSearchingCEP] = useState(false);
  const [formData, setFormData] = useState<AdvogadoData>(emptyFormData());
  const [signatureMode, setSignatureMode] = useState<'drawn' | 'typed'>('drawn');
  const [typedSignatureName, setTypedSignatureName] = useState('');
  const [redrawSignature, setRedrawSignature] = useState(true);
  const signaturePadRef = useRef<SignaturePadHandle>(null);

  const notifications = useNotifications();
  const confirm = useConfirm();

  useEffect(() => {
    fetchAdvogados();
  }, []);

  const fetchAdvogados = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/advogado/all');
      setAdvogados(response.data);
    } catch (error) {
      console.error('Erro ao buscar advogados:', error);
      notifications.error.settingsUpdate('Erro ao carregar advogados');
    } finally {
      setLoading(false);
    }
  };

  const startCreate = () => {
    setEditingId(null);
    setFormData(emptyFormData());
    setLogoPreview(null);
    setSignatureMode('drawn');
    setTypedSignatureName('');
    setRedrawSignature(true);
    setIsEditing(true);
  };

  const startEdit = (adv: AdvogadoData) => {
    setEditingId(adv._id || null);
    setFormData({ ...adv, endereco: { ...adv.endereco } });
    setLogoPreview(adv.logo || null);
    setSignatureMode(adv.assinatura?.tipo === 'typed' ? 'typed' : 'drawn');
    setTypedSignatureName(adv.assinatura?.typedName || '');
    setRedrawSignature(!adv.assinatura?.tipo);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData(emptyFormData());
    setLogoPreview(null);
    setSignatureMode('drawn');
    setTypedSignatureName('');
    setRedrawSignature(true);
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    if (field.startsWith('endereco.')) {
      const enderecoField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          [enderecoField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notifications.error.settingsUpdate('Apenas arquivos de imagem são permitidos');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notifications.error.settingsUpdate('Arquivo muito grande. Máximo 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      const uploadData = new FormData();
      uploadData.append('logo', file);

      const response = await axios.post('/advogado/upload-logo', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const logoUrl = response.data.logoUrl;
      setFormData(prev => ({ ...prev, logo: logoUrl }));
      setLogoPreview(logoUrl);
      notifications.success.settingsUpdated();
    } catch (error) {
      console.error('Erro ao fazer upload do logo:', error);
      notifications.error.settingsUpdate('Erro ao fazer upload do logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo: '' }));
    setLogoPreview(null);
  };

  const searchCEP = async (cep: string) => {
    if (!cep || cep.length < 8) return;

    try {
      setSearchingCEP(true);
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);

      if (response.data.erro) {
        notifications.error.settingsUpdate('CEP não encontrado');
        return;
      }

      const { logradouro, bairro, localidade } = response.data;

      setFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          logradouro: logradouro || '',
          bairro: bairro || '',
          cidade: localidade || '',
          cep
        }
      }));

      notifications.success.settingsUpdated();
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      notifications.error.settingsUpdate('Erro ao buscar CEP');
    } finally {
      setSearchingCEP(false);
    }
  };

  const buildAssinaturaPayload = () => {
    if (signatureMode === 'drawn') {
      if (redrawSignature) {
        if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
          return { tipo: 'drawn' as const, imageData: signaturePadRef.current.toDataURL(), typedName: '' };
        }
        return { tipo: null, imageData: '', typedName: '' };
      }
      return formData.assinatura || { tipo: null, imageData: '', typedName: '' };
    }
    if (typedSignatureName.trim()) {
      return { tipo: 'typed' as const, imageData: '', typedName: typedSignatureName.trim() };
    }
    return { tipo: null, imageData: '', typedName: '' };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = { ...formData, assinatura: buildAssinaturaPayload() };

      if (editingId) {
        await axios.put(`/advogado/${editingId}`, payload);
        notifications.success.settingsUpdated();
      } else {
        await axios.post('/advogado', payload);
        notifications.success.settingsUpdated();
      }

      cancelEdit();
      fetchAdvogados();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error('Erro ao salvar advogado:', error);
      notifications.error.settingsUpdate(
        err.response?.data?.message || 'Erro ao salvar dados do advogado'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: 'Excluir advogado', message: 'Este advogado será removido permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;

    try {
      await axios.delete(`/advogado/${id}`);
      notifications.success.settingsUpdated();
      if (editingId === id) cancelEdit();
      fetchAdvogados();
    } catch (error) {
      console.error('Erro ao deletar advogado:', error);
      notifications.error.settingsUpdate('Erro ao deletar advogado');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Breadcrumbs />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('admin.lawyers')}</h1>
          <p className="text-gray-600 mt-1">
            Cadastre vários advogados com logo. Na geração de documentos NIF, escolha qual utilizar.
          </p>
        </div>
        {!isEditing && (
          <Button
            onClick={startCreate}>
            <Plus className="w-4 h-4" />
            <span>Adicionar Advogado</span>
          </Button>
        )}
      </div>

      {!isEditing && advogados.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 shrink-0" />
            <p className="text-yellow-800">
              Nenhum advogado cadastrado. Clique em &quot;Adicionar Advogado&quot; para começar.
            </p>
          </div>
        </div>
      )}

      {!isEditing && advogados.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {advogados.map(adv => (
            <div
              key={adv._id}
              className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100"
            >
              <div className="p-5 border-b bg-gray-50 flex items-start gap-4">
                {adv.logo ? (
                  <img
                    src={getAssetUrl(adv.logo)}
                    alt={`Logo ${adv.nome}`}
                    className="w-16 h-16 object-contain border border-gray-200 rounded-lg bg-white shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center shrink-0">
                    <User className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 truncate">{adv.nome}</h3>
                  {(adv.oabNumero || adv.oabEstado) && (
                    <p className="text-sm text-gray-600">
                      OAB {adv.oabNumero}{adv.oabEstado ? `/${adv.oabEstado}` : ''}
                    </p>
                  )}
                  {adv.iban && (
                    <p className="text-xs text-gray-500 truncate" title={adv.iban}>IBAN: {adv.iban}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {adv.endereco.cidade}, {adv.endereco.pais}
                  </p>
                </div>
              </div>

              <div className="p-5 space-y-2 text-sm">
                <p>
                  <span className="text-gray-500">Cédula:</span>{' '}
                  <span className="font-medium">{adv.cedulaProfissional}</span>
                </p>
                {adv.telefone && (
                  <p>
                    <span className="text-gray-500">Tel:</span>{' '}
                    <span className="font-medium">{adv.telefone}</span>
                  </p>
                )}
                {adv.email && (
                  <p className="truncate">
                    <span className="text-gray-500">Email:</span>{' '}
                    <span className="font-medium">{adv.email}</span>
                  </p>
                )}
              </div>

              <RowActions align="end" className="mx-5 mb-5">
                <ActionButton action="edit" onClick={() => startEdit(adv)} />
                <ActionButton action="delete" onClick={() => handleDelete(adv._id!)} />
              </RowActions>
            </div>
          ))}
        </div>
      )}

      {isEditing && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-2 mb-6">
            <CheckCircle className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {editingId ? 'Editar Advogado' : 'Novo Advogado'}
            </h2>
          </div>

          <form
            onSubmit={e => {
              e.preventDefault();
              handleSave();
            }}
            className="space-y-6"
          >
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Image className="w-5 h-5 mr-2" />
                Logo
              </h3>
              <div className="space-y-4">
                {logoPreview && (
                  <div className="flex items-center space-x-4">
                    <img
                      src={getAssetUrl(logoPreview)}
                      alt="Preview do Logo"
                      className="w-24 h-24 object-contain border border-gray-300 rounded-lg"
                    />
                    <ActionButton action="remove" labelVisibility="always" onClick={removeLogo} />
                  </div>
                )}
                <div className="flex items-center space-x-4 flex-wrap gap-y-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploadingLogo}
                    />
                    <div className={`btn-primary inline-flex items-center gap-2 ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      {uploadingLogo ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          <span>Enviando...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          <span>{logoPreview ? 'Alterar Logo' : 'Adicionar Logo'}</span>
                        </>
                      )}
                    </div>
                  </label>
                  <p className="text-sm text-gray-500">JPG, PNG ou GIF — máx. 5MB</p>
                </div>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <PenTool className="w-5 h-5 mr-2" />
                Assinatura do advogado
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Ao gerar o contrato, esta assinatura já aparece na linha do CONTRATADO — restando apenas a assinatura do cliente.
              </p>

              <Tabs
                variant="segmented"
                className="mb-4"
                value={signatureMode}
                onChange={setSignatureMode}
                items={[
                  { id: 'drawn', label: 'Desenhar assinatura' },
                  { id: 'typed', label: 'Digitar nome' },
                ]}
              />

              {signatureMode === 'drawn' ? (
                !redrawSignature && formData.assinatura?.tipo === 'drawn' && formData.assinatura.imageData ? (
                  <div className="flex items-center gap-4">
                    <img
                      src={formData.assinatura.imageData}
                      alt="Assinatura salva"
                      className="h-20 border border-gray-300 rounded-lg bg-white px-3"
                    />
                    <button
                      type="button"
                      onClick={() => setRedrawSignature(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Trocar assinatura
                    </button>
                  </div>
                ) : (
                  <SignaturePad ref={signaturePadRef} />
                )
              ) : (
                <input
                  type="text"
                  value={typedSignatureName}
                  onChange={e => setTypedSignatureName(e.target.value)}
                  placeholder="Digite o nome como deve aparecer assinado"
                  style={{ fontFamily: "'Brush Script MT', cursive" }}
                  className="field text-2xl"
                />
              )}
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados Pessoais</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={e => handleInputChange('nome', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cédula profissional</label>
                  <input
                    type="text"
                    value={formData.cedulaProfissional}
                    onChange={e => handleInputChange('cedulaProfissional', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número OAB</label>
                  <input
                    type="text"
                    value={formData.oabNumero}
                    onChange={e => handleInputChange('oabNumero', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado OAB</label>
                  <input
                    type="text"
                    value={formData.oabEstado}
                    onChange={e => handleInputChange('oabEstado', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input
                    type="tel"
                    value={formData.telefone || ''}
                    onChange={e => handleInputChange('telefone', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={e => handleInputChange('email', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IBAN para pagamentos</label>
                  <input
                    type="text"
                    value={formData.iban || ''}
                    onChange={e => handleInputChange('iban', e.target.value)}
                    placeholder="PT50..."
                    className="field"
                  />
                  <p className="text-xs text-gray-500 mt-1">Aparece na cláusula 3.1 dos contratos IRN.</p>
                </div>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Endereço
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.endereco.cep}
                      onChange={e => {
                        const cep = e.target.value.replace(/\D/g, '');
                        handleInputChange('endereco.cep', cep);
                        if (cep.length === 8) searchCEP(cep);
                      }}
                      placeholder="00000-000"
                      maxLength={9}
                      className="field pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => searchCEP(formData.endereco.cep)}
                      disabled={searchingCEP || formData.endereco.cep.length < 8}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200 disabled:opacity-50"
                    >
                      {searchingCEP ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro</label>
                  <input
                    type="text"
                    value={formData.endereco.logradouro}
                    onChange={e => handleInputChange('endereco.logradouro', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <input
                    type="text"
                    value={formData.endereco.numero}
                    onChange={e => handleInputChange('endereco.numero', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input
                    type="text"
                    value={formData.endereco.complemento || ''}
                    onChange={e => handleInputChange('endereco.complemento', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                  <input
                    type="text"
                    value={formData.endereco.bairro}
                    onChange={e => handleInputChange('endereco.bairro', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <input
                    type="text"
                    value={formData.endereco.cidade}
                    onChange={e => handleInputChange('endereco.cidade', e.target.value)}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">País</label>
                  <input
                    type="text"
                    value={formData.endereco.pais}
                    onChange={e => handleInputChange('endereco.pais', e.target.value)}
                    className="field"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="sm:pr-4">
                  <h4 className="text-sm font-medium text-gray-900">Ocultar dados profissionais no contrato</h4>
                  <p className="text-sm text-gray-600">
                    Ao gerar o Contrato de Assessoria ou a Procuração, oculta Número OAB, Cédula profissional, Estado OAB,
                    CEP, Logradouro, Número, Complemento e Bairro — mostra apenas Cidade e País.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={!!formData.ocultarDadosContrato}
                    onChange={e => handleInputChange('ocultarDadosContrato', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <Button variant="ghost" size="sm"
                type="button"
                onClick={cancelEdit}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}>
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Salvar</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
    </div>
  );
};

export default AdminAdvogado;
